/**
 * browserBackStep — make the BROWSER's Back button close an in-app sub-view,
 * instead of leaving the application (Card PIPELINE-PLACE-AND-FOCUS, 10 Oct 2026).
 *
 * ── ⛔⛔ THE MEASURED STATE THIS FIXES ──────────────────────────────────────
 *
 * Measured on `main` @ d0cc155: `grep -rn popstate src public portal main.js
 * preload.js` returns **zero hits**, and the only `history.*` writes in the
 * whole tree are `replaceState` (src/lib/urlState.js, src/pages/crm/
 * ContactsTab.js). replaceState creates NO history entry.
 *
 * So pressing the browser's Back chevron — or a mouse's back thumb button, or
 * swiping back on a trackpad — inside the CRM did not close the open contact.
 * It popped whatever entry preceded the application and navigated AWAY from
 * it. Observed in a real browser: `about:blank`.
 *
 * ── ⚠ WHY THIS IS NOT "JUST USE pushState IN NavHistory" ────────────────────
 *
 * src/lib/urlState.js states the constraint plainly, and it is right:
 *
 *   > ⛔ replaceState, never pushState. Navigating inside the app already
 *   > pushes a NavHistory entry […]; also pushing browser history would make
 *   > the hardware/browser Back button walk a SECOND, DIFFERENTLY-SHAPED STACK
 *   > and fight the in-app one.
 *
 * Mirroring the entire 15-entry NavHistory stack into browser history would
 * mean reconciling two stacks on every navigate, back, jumpTo and dirty-form
 * refusal — app-wide blast radius for a CRM card. This hook does the much
 * smaller thing instead: it holds AT MOST ONE browser history entry while the
 * sub-view is open, so the browser stack is a strict SUBSET of the in-app one
 * rather than a second stack fighting it.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * ⛔⛔ THE INVARIANT: THIS MODULE NEVER TRAVERSES HISTORY.
 * ══════════════════════════════════════════════════════════════════════════
 *
 *   **`history.go()` is never called. Not on close, not on unmount, not ever.**
 *
 * That is stronger than the rule this module carried in review round 2 ("go(-1)
 * and pushState are never both called in one turn"), and it is stronger for a
 * reason: that rule was scoped to ONE HOOK INSTANCE, and the race is not.
 *
 * ⚠⚠ REVIEW ROUND 3 (Cowork, PR #620, 11 Oct 2026) — `history.go(-1)` is
 * ASYNCHRONOUS. It queues a traversal. **Anything that `replaceState`s the top
 * entry before that queue drains changes what the traversal lands on**, and in
 * this app three different things do exactly that in the very same commit:
 *
 *   SIGN-OUT / IDLE TIMEOUT   App.js clears every place key on the top entry
 *   LEAVING THE CRM           App.js writes `?page=<the new page>`
 *   CLOSING THE LAST CONTACT  CRMPage writes the closed-contact URL
 *
 * In each case the traversal then landed on the entry BELOW, which still
 * carried `?page=crm&tab=…&contact=<id>&pipeline=…&stage=…` because the push
 * had copied that URL. MEASURED IN CHROME on the previous head:
 *
 *   · after SIGN-OUT the address bar still read
 *     `page=crm tab=pipeline contact=5 ctab=conversations pipeline=1 stage=1`
 *     — one user's open record surviving into the next user's browser;
 *   · after closing a contact in-app, `?contact=` remained and a RELOAD
 *     reopened a record the person had just closed;
 *   · after navigating to Settings the app rendered `settings` while the URL
 *     said `page=crm`.
 *
 * None of those is recoverable by being careful about ordering, because the
 * competing writer is a different component. So the traversal is gone.
 *
 * ── WHAT REPLACES IT: ADOPT-OR-PUSH, AND NEVER CONSUME ─────────────────────
 *
 *   ARM        CANONICALISE the entry we are about to leave behind, then adopt
 *              or push. If the top entry already carries a marker MINTED BY
 *              THIS SESSION, adopt it; otherwise push a new one. One entry, no
 *              matter how many times the family opens, closes and reopens.
 *   SWITCH     `active` does not change ⇒ nothing is called at all.
 *   BACK       popstate; the browser removed the entry for us. Close the
 *              active record, then re-arm if another is still open.
 *   CLOSE /    Release the hold and LEAVE THE ENTRY ALONE. The URL is owned by
 *   UNMOUNT    whoever writes it (CRMPage clears its own keys on unmount, App
 *              clears everything on sign-out) — this module does not fight them
 *              for it.
 *
 * ⚠ ACCEPTED AND DELIBERATE (review round 5): after leaving the CRM for a page
 * that renders the SAME component — crm -> my-day — with a contact open,
 * pressing Back returns to the CRM with that contact STILL OPEN. The workspace
 * is what holds open records (src/lib/workspaceTabs.js keeps 5-10 of them
 * across reloads by design), the URL says `contact=` and the screen shows it,
 * so the two agree. That is the behaviour the product had before this card and
 * it is not changed here.
 *
 * ⚠ THE COST, STATED: a dead history entry can be left behind, so one Back
 * press may do nothing visible before the next one moves. That is the trade the
 * reviewer named — "a dead extra entry is acceptable, a wrong URL or a
 * cross-user leak is not" — and adopt-or-push keeps the count at one rather
 * than one per open.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * ⛔⛔ THE SECOND INVARIANT: THE ENTRY UNDERNEATH IS CANONICALISED FIRST.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Because we never traverse, the ONLY way past our entry is the person pressing
 * Back, and they land on whatever the URL was WHEN WE PUSHED. Round 3 tried to
 * get that right by CALL ORDER alone — pushing before the sub-view's params
 * reached the address bar. ⚠⚠ REVIEW ROUND 4 (Cowork, 11 Oct 2026) showed that
 * is not enough, in two shapes that call order cannot reach:
 *
 *   A MOUNT THAT OPENS IMMEDIATELY   a workspace restored from localStorage, or
 *        a `?contact=` deep link, opens a record on the FIRST commit. React runs
 *        child effects before parent ones, so the push happened before App wrote
 *        `?page=`, and the entry underneath kept the PREVIOUS page — or, for a
 *        deep link, kept `contact=` because it was in the URL before React ran.
 *   MEASURED: Back then landed on `?page=settings` while the screen showed the
 *        CRM. The address bar and the screen disagreed, and a reload went
 *        somewhere else again.
 *
 * So the caller now supplies `canonicalise()`, which this module runs
 * SYNCHRONOUSLY IMMEDIATELY BEFORE the push. Its job is to leave the current
 * entry saying "this page, nothing open" — whatever the effect order was and
 * whatever a pasted link put there. Nothing is canonicalised when ADOPTING: we
 * are not creating a new entry underneath, so there is nothing to fix.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * ⛔⛔ THE THIRD INVARIANT: A MARKER BELONGS TO ONE SIGNED-IN SESSION.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Round 3 adopted ANY numeric marker it found. ⚠⚠ Round 4: `history.state`
 * survives a sign-out — src/lib/urlState.js deliberately CARRIES it through
 * every replaceState (so the marker is not erased mid-session), and a sign-out
 * rewrites only the top entry's query string. MEASURED: after an in-session
 * sign-out the marker was still sitting there, so the NEXT user's first
 * CRMPage mount would adopt the previous user's entry.
 *
 * Markers are therefore stamped with the caller's `sessionNonce` and adopted
 * ONLY on an exact match. An absent nonce adopts nothing — a hook that cannot
 * prove whose session it is in pushes its own entry rather than inheriting a
 * stranger's. `clearBrowserBackMarker()` additionally strips the marker on
 * sign-out, so the two defences are independent.
 *
 * ── ⚠ React.StrictMode ─────────────────────────────────────────────────────
 *
 * The double-invoke is safe BY CONSTRUCTION here, which was not true of the
 * previous version: mount pushes, the simulated unmount only releases, and the
 * second mount ADOPTS the entry the first one pushed. Net effect, one entry —
 * the same as a single mount. (This app does not enable StrictMode today; the
 * property is cheap and worth not having to re-derive.)
 *
 * ══════════════════════════════════════════════════════════════════════════
 * ⛔⛔ THE FOURTH INVARIANT: EVERY ENTRY WE WRITE IS OWNED BY A LOGIN.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * ⚠⚠ REVIEW ROUND 5. The session nonce on the MARKER secured the entry we hold.
 * It did nothing for the rest of the stack, and the rest of the stack is where
 * the leak lived: A opens a record and presses Back, which closes it and leaves
 * a FORWARD entry still carrying `contact=`, the pipeline and the stage. Sign-
 * out rewrites only the entry it stands on. MEASURED in Chrome and WebKit — B
 * signs in in the same document, presses Forward, and A's record opens in B's
 * workspace and is written to B's localStorage.
 *
 * So the push is stamped by `src/lib/loginNonce.js` like every other entry this
 * app writes, and the readers (App.js on popstate and on the cold-load restore)
 * adopt nothing from an entry stamped by a different login. Read that module
 * for why the nonce lives in sessionStorage and why an UNOWNED entry is fine.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * ⛔⛔ AND BACK MUST NOT THROW AWAY UNSAVED WORK.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Wiring this hook SILENTLY REMOVED A WARNING. Before it, pressing browser Back
 * left the document and ContactDetailView's `beforeunload` asked about a
 * half-written note. The traversal is now same-document, so that never fires —
 * and this module was closing the record, and the note with it, unasked.
 *
 * `onBack` may therefore return FALSE to mean "the person declined". The entry
 * is pushed back, the record stays open, and the caller restores its own URL
 * keys — it is the only thing that knows what they should say. Anything other
 * than an explicit `false` (including `undefined`, which is what every caller
 * returned before this round) means ALLOW.
 *
 * ⚠ WHY THE MARKER STILL EXISTS NOW THAT NOTHING IS CONSUMED. It no longer
 * guards a traversal — there is none. It answers a different question: "is the
 * entry I am standing on one I already own?", which is what lets ARM adopt
 * instead of pushing a second entry, and it carries the session nonce above.
 * src/lib/urlState.js CARRIES history.state through every replaceState so the
 * marker survives the query-string rewrites that happen while a record is open
 * — and `clearBrowserBackMarker()` is what removes it deliberately, at
 * sign-out.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { IS_CAPACITOR } from './platform';
import { stampOwner } from './loginNonce';
import { useNavNonce } from './navNonce';

/** Marker key in history.state. Namespaced so it cannot collide with anything else. */
export const STEP_STATE_KEY = 'zgBrowserStep';

let seq = 0;

/**
 * How many mounted hooks are currently holding an entry.
 *
 * ⛔ READ BY App.js ON popstate — review round 5, minor item 3. After Back
 * closes a record and the person presses FORWARD, they land on the entry that
 * held it, still carrying `?contact=`, with nothing open on screen and no hook
 * holding anything. A reload from there reopened a record the person had
 * closed. App scrubs that entry, and this is how it can tell the difference
 * between "a contact the screen has open" (a hold exists) and "a leftover in
 * the URL" (no hold).
 */
let holds = 0;
export function isHoldingBrowserStep() { return holds > 0; }

/**
 * Register ONE browser-history Back step for a whole sub-view family.
 *
 * ⛔ CALL THIS ONCE, AT THE OWNER OF THE FAMILY — not per open record. See the
 * race above: a per-record owner pops and pushes on every switch. For the CRM
 * that owner is CRMPage, which knows about the whole workspace;
 * tests/react/CrmUrlPlaceWiring.test.js asserts the consumer set.
 *
 * @param {object}   opts
 * @param {boolean}  opts.active   true while ANY record in the family is open
 * @param {Function} opts.onBack   close the ACTIVE record. Called at most once
 *                                 per entry, and never for one the app closed
 *                                 itself.
 * @param {string}   [opts.sessionNonce] identifies the signed-in session. A
 *                                 marker is adopted only when it carries this
 *                                 exact value; absent, nothing is adopted.
 * @param {Function} [opts.canonicalise] run synchronously just before the push,
 *                                 to leave the entry underneath saying "this
 *                                 page, nothing open".
 * @param {boolean}  [opts.enabled=true] escape hatch for tests / platforms
 */
export default function useBrowserBackStep({
  active, onBack, sessionNonce = undefined, canonicalise = null, enabled = true,
}) {
  /*
   * ⛔⛔ THE NONCE COMES FROM CONTEXT WHEN THE CALLER DOES NOT SUPPLY ONE
   * (Card P194).
   *
   * Adoption requires proving ownership (`mk.s === sessionNonce`), and this
   * file's own docblock states what happens without it: "An absent nonce
   * adopts nothing — a hook that cannot prove ownership pushes its own entry
   * instead." Measured 31 Oct: 20 of 23 call sites passed none, so each pushed
   * a history entry per open that could never be reused. That is the mechanism
   * which closed the CEO drill-down modal (#766).
   *
   * ⚠ AN EXPLICIT PROP STILL WINS. App.js, CRMPage.js and DrilldownModal.js
   * pass one deliberately and are unchanged; `undefined` (not '') is the
   * "nothing supplied" signal, so a caller can still pass '' to mean "I really
   * do own nothing" and get the old behaviour.
   *
   * ⚠ The provider carries App's OWN sessionNonce, so every caller lands in
   * ONE namespace. Defaulting to loginNonce's currentLoginNonce() instead
   * would have been a second namespace — see src/lib/navNonce.js.
   */
  const contextNonce = useNavNonce();
  const resolvedNonce = sessionNonce === undefined ? contextNonce : sessionNonce;
  // The handler is read through a ref so that re-rendering with a new closure
  // never re-pushes a history entry. This is the same reason NavHistory keeps
  // its guard predicates in refs: the effect below must depend on `active`
  // ALONE (plus the re-arm tick), or an inline arrow in the caller's JSX would
  // push on every render.
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;
  const canonicaliseRef = useRef(canonicalise);
  canonicaliseRef.current = canonicalise;

  /**
   * The id of the entry we are holding, or 0 for none.
   *
   * ⭐ It is the "am I armed?" flag, nothing more — no traversal hangs off it
   * any more. Zeroed the instant popstate tells us the browser took the entry,
   * so a re-arm mints or adopts cleanly, and zeroed again on release.
   */
  const heldRef = useRef(0);

  // Bumped after a popstate so the effect below re-runs and can arm a FRESH
  // entry when a record is still open. `active` alone cannot do that: closing
  // contact B while contact A stays open leaves it true, so the effect would
  // never re-run and the next Back would leave the app with A still open.
  const [armTick, setArmTick] = useState(0);
  const rearm = useCallback(() => setArmTick((t) => t + 1), []);

  useEffect(() => {
    if (!active || !enabled) return undefined;
    // Inert on Android/iOS shells: NavHistory already routes the hardware
    // button through back(), and a second listener would close two steps.
    if (IS_CAPACITOR) return undefined;
    if (typeof window === 'undefined' || !window.history || !window.addEventListener) return undefined;

    if (heldRef.current === 0) {
      /*
       * ⭐ ADOPT BEFORE PUSHING. A marker already on the top entry is ours from
       * an earlier open (we never remove it, because removing it would mean
       * writing history we do not own). Adopting keeps the count at ONE entry
       * however many times a record is opened and closed, and it is what makes
       * a StrictMode double-invoke — and a crm/my-day remount, which unmounts
       * and mounts CRMPage in a single commit — add nothing.
       */
      let existing = null;
      try {
        const st = window.history.state;
        const mk = st && st[STEP_STATE_KEY];
        // ⛔ OURS ONLY, AND THIS SESSION'S ONLY. A marker with a different
        // nonce belongs to whoever was signed in before; inheriting it is how
        // one person's open record reaches the next person's browser.
        if (mk && typeof mk === 'object' && resolvedNonce && mk.s === resolvedNonce
            && typeof mk.id === 'number') existing = mk.id;
      } catch { /* history unavailable */ }

      if (existing != null) {
        heldRef.current = existing;
        holds += 1;
      } else {
        const id = ++seq;
        try {
          /*
           * ⛔⛔ FIX THE ENTRY WE ARE ABOUT TO LEAVE BEHIND, THEN PUSH.
           * See the second invariant at the top of the file: this is the only
           * entry a Back press can reach past ours, so it has to say "this
           * page, nothing open" before it becomes unreachable to us.
           */
          if (canonicaliseRef.current) {
            try { canonicaliseRef.current(); } catch { /* best-effort */ }
          }
          // Same URL, one more entry — the query string is whatever
          // canonicalise() just left. This pushes a PLACE IN HISTORY, not a
          // route.
          // ⛔ stampOwner: this entry belongs to the login in force right now,
          // like every other entry the app writes. See src/lib/loginNonce.js.
          window.history.pushState(
            stampOwner({ ...(window.history.state || {}), [STEP_STATE_KEY]: { id, s: resolvedNonce } }),
            '',
            window.location.href
          );
          heldRef.current = id;
          holds += 1;
        } catch {
          // No history (a very old browser, a sandboxed frame). The in-app Back
          // control still works; this hook simply adds nothing.
          return undefined;
        }
      }
    }

    const onPop = () => {
      if (heldRef.current === 0) return;   // not ours, or already released
      heldRef.current = 0;                 // the BROWSER removed it
      holds = Math.max(0, holds - 1);

      /*
       * ⛔⛔ THE UNSAVED-WORK GUARD — review round 5, major item 2.
       *
       * Before this card, pressing browser Back LEFT THE DOCUMENT, and
       * ContactDetailView's `beforeunload` warned about a half-written note.
       * Making the traversal same-document silently removed that warning: this
       * handler closed the record, and the note went with it, unasked.
       *
       * `onBack` may now return FALSE to mean "the person said no". When it
       * does, the record stays open and the entry is put back, so the screen
       * and the address bar stay exactly where they were. The caller is
       * responsible for restoring its own URL keys before returning false —
       * it is the only thing that knows what they should say.
       */
      let refused = false;
      try { refused = onBackRef.current && onBackRef.current() === false; }
      catch { /* best-effort */ }

      if (refused) {
        try {
          const id = ++seq;
          window.history.pushState(
            stampOwner({ ...(window.history.state || {}), [STEP_STATE_KEY]: { id, s: resolvedNonce } }),
            '',
            window.location.href
          );
          heldRef.current = id;
          holds += 1;
        } catch { /* history unavailable — the record is still open, which is what matters */ }
        return;
      }

      // Ask for a re-arm. Whether one actually happens is decided by `active`
      // on the next render — i.e. by whether a record is still open — rather
      // than guessed here, where the close has not been committed yet.
      rearm();
    };
    window.addEventListener('popstate', onPop);

    return () => {
      window.removeEventListener('popstate', onPop);
      /*
       * ⛔⛔ RELEASE, NEVER TRAVERSE. See the invariant at the top of the file.
       *
       * This cleanup runs when the last record closes, when CRMPage unmounts
       * for a sign-out, an idle timeout, or a navigation to another page — and
       * in EVERY one of those commits something else is rewriting the top
       * entry. A `history.go(-1)` here would be a queued traversal racing that
       * write, and it would land on an entry still carrying the open record.
       * That is the cross-user leak and the two desync defects, all from this
       * one line. There is no ordering that makes it safe, because the
       * competing writer is a different component.
       */
      if (heldRef.current !== 0) holds = Math.max(0, holds - 1);
      heldRef.current = 0;
    };
  // ⚠ resolvedNonce, not sessionNonce: the effect must re-run when the value
  // it STAMPS changes, and for a context consumer that value arrives from the
  // provider rather than from props.
  }, [active, enabled, armTick, rearm, resolvedNonce]);
}

/**
 * ⛔⛔ Strip the marker from the entry we are standing on. Called by App.js on
 * sign-out and on an idle timeout.
 *
 * `history.state` outlives a sign-out: src/lib/urlState.js carries it through
 * every `replaceState` on purpose, and the sign-out itself only rewrites the
 * query string. MEASURED in Chrome and WebKit on the previous head — the
 * marker was still there after signing out, so the next person's first mount
 * would adopt the previous person's entry.
 *
 * ⚠ This is the SECOND of two independent defences; `sessionNonce` is the
 * first. Either alone would close the hole, and keeping both means a future
 * caller that forgets to pass a nonce still does not inherit a stranger's
 * entry. It rewrites ONLY `history.state`, never the URL — the place keys are
 * cleared by App.js through urlState's allowlist, which is the only thing
 * permitted to write that query string.
 */
export function clearBrowserBackMarker() {
  try {
    const st = window.history.state;
    if (!st || st[STEP_STATE_KEY] === undefined) return;
    const next = { ...st };
    delete next[STEP_STATE_KEY];
    window.history.replaceState(next, '', window.location.href);
  } catch { /* history unavailable */ }
}
